/**
 * Oggi (OTTO.md §4.4).
 *
 * La giornata di un property manager: chi arriva, chi parte, chi pulisce, chi
 * aspetta una risposta. È la schermata che si guarda alle otto del mattino,
 * quindi dice le cose nell'ordine in cui servono: quattro numeri per capire
 * com'è messo il portafoglio, poi la giornata in una lista sola con i filtri,
 * poi la settimana che viene.
 */

import { useEffect, useMemo, useState } from "react";
import {
  Building2,
  CalendarRange,
  ChevronLeft,
  ChevronRight,
  Clock,
  Euro,
  Inbox,
  ListChecks,
  LogIn,
  LogOut,
  Percent,
  Sparkles,
  Sun,
  Wrench,
} from "lucide-react";
import {
  addDays,
  formatDate,
  formatEur,
  nomeMese,
  todayInRome,
  weekday,
  type DayOverview,
  type IsoDate,
  type ListingView,
  type ProfittoPortafoglio,
  type SuggestionView,
} from "@otto/shared";
import { api } from "../lib/api.ts";
import { useStore } from "../lib/store.tsx";
import {
  Avatar,
  Card,
  EmptyState,
  Pill,
  Segmented,
  Skeleton,
  cx,
} from "../ui/primitives.tsx";
import { GrigliaKpi, IntestazioneRiquadro, Kpi } from "../ui/Sezione.tsx";

const GIORNI = ["dom", "lun", "mar", "mer", "gio", "ven", "sab"];

type Filtro = "tutto" | "arrivi" | "partenze" | "lavori";

export function Today({
  onGoTo,
}: {
  onGoTo: (view: "chat" | "inbox" | "suggestions") => void;
}) {
  const store = useStore();
  const oggi = todayInRome();
  const [date, setDate] = useState(oggi);
  const [overview, setOverview] = useState<DayOverview | null>(null);
  const [suggestions, setSuggestions] = useState<SuggestionView[]>([]);
  const [settimana, setSettimana] = useState<DayOverview[] | null>(null);
  const [listings, setListings] = useState<ListingView[] | null>(null);
  const [conto, setConto] = useState<ProfittoPortafoglio | null>(null);
  const [filtro, setFiltro] = useState<Filtro>("tutto");

  useEffect(() => {
    setOverview(null);
    void api
      .overview(date)
      .then(({ overview: loaded }) => setOverview(loaded))
      .catch(() => setOverview(null));
  }, [date]);

  useEffect(() => {
    void api
      .suggestions()
      .then(({ suggestions: loaded }) => setSuggestions(loaded))
      .catch(() => setSuggestions([]));

    // I sette giorni davanti, tutti insieme: sono sette letture su una cache
    // locale, e valgono la risposta a "e domani?".
    void Promise.all(
      Array.from({ length: 7 }, (_, index) =>
        api.overview(addDays(oggi, index)),
      ),
    )
      .then((risposte) => setSettimana(risposte.map((r) => r.overview)))
      .catch(() => setSettimana(null));

    void api
      .listings()
      .then(({ listings: caricati }) => setListings(caricati))
      .catch(() => setListings(null));

    // I soldi di tutte le case insieme, in una chiamata sola. Arriva dopo il
    // resto apposta: la giornata deve comparire subito.
    void api
      .portafoglio()
      .then(({ portafoglio: caricato }) => setConto(caricato))
      .catch(() => setConto(null));
  }, [oggi]);

  const etichetta =
    date === addDays(oggi, 1)
      ? "Domani"
      : date === addDays(oggi, -1)
        ? "Ieri"
        : null;

  const daDecidere =
    (overview?.awaitingReplies ?? 0) + (overview?.pendingActions ?? 0);

  /**
   * Il colpo d'occhio sul portafoglio: medie sugli immobili, non somme.
   * "Quanto sono pieno" e "a quanto vendo" sono le due domande che un
   * property manager si fa prima del caffè.
   */
  const portafoglio = useMemo(() => {
    if (!listings || listings.length === 0) return null;
    const occupazioni = listings
      .map((l) => l.stats?.occupancyPercent)
      .filter((v): v is number => v != null);
    const prezzi = listings
      .map((l) => l.stats?.avgPriceCents)
      .filter((v): v is number => v != null);
    const attivi = listings.filter((l) => l.channels.length > 0).length;

    // Il periodo sta accanto al numero: «occupazione media 74%» senza dire
    // di quando è non si confronta con niente, nemmeno con se stesso il mese
    // dopo. Lo portano le stesse righe da cui viene la media.
    const periodo = listings.find((l) => l.stats)?.stats?.periodo ?? null;

    return {
      immobili: listings.length,
      attivi,
      mese: periodo ? nomeMese(periodo.da) : null,
      occupazione: occupazioni.length
        ? Math.round(
            occupazioni.reduce((a, b) => a + b, 0) / occupazioni.length,
          )
        : null,
      prezzo: prezzi.length
        ? Math.round(prezzi.reduce((a, b) => a + b, 0) / prezzi.length)
        : null,
      piene: occupazioni.filter((o) => o >= 70).length,
    };
  }, [listings]);

  // La giornata in una lista sola: arrivi, partenze e lavori nello stesso
  // ordine in cui capitano, con il filtro per guardarne un tipo alla volta.
  const eventi = useMemo(() => {
    if (!overview) return [];
    const arrivi = overview.checkIns.map((r) => ({
      id: `in-${r.listingId}-${r.guestName}`,
      tipo: "arrivi" as const,
      titolo: r.guestName,
      sotto: r.listingNickname,
      ora: r.time,
      fatto: false,
      chi: null as string | null,
    }));
    const partenze = overview.checkOuts.map((r) => ({
      id: `out-${r.listingId}-${r.guestName}`,
      tipo: "partenze" as const,
      titolo: r.guestName,
      sotto: r.listingNickname,
      ora: r.time,
      fatto: false,
      chi: null as string | null,
    }));
    const lavori = overview.tasks.map((t) => ({
      id: `task-${t.id}`,
      tipo: "lavori" as const,
      titolo: t.titolo,
      sotto: t.listingNickname,
      ora: null as string | null,
      fatto: t.stato === "completato",
      chi: t.assegnatoA ?? null,
    }));
    const tutti = [...partenze, ...arrivi, ...lavori];
    return filtro === "tutto" ? tutti : tutti.filter((e) => e.tipo === filtro);
  }, [overview, filtro]);

  const giornataVuota =
    overview &&
    overview.checkIns.length === 0 &&
    overview.checkOuts.length === 0 &&
    overview.tasks.length === 0;

  return (
    <div className="flex flex-col gap-6">
      {/* ── Come sto andando: tutte le case, questo mese ───────────────── */}
      <ContoDelMese conto={conto} />

      {/* ── Il portafoglio in quattro numeri ──────────────────────────── */}
      <GrigliaKpi>
        <Kpi
          icon={<Building2 size={17} />}
          etichetta="Immobili"
          valore={portafoglio ? String(portafoglio.immobili) : ""}
          dettaglio={
            portafoglio ? `${portafoglio.attivi} pubblicati sui canali` : ""
          }
          caricamento={!portafoglio}
        />
        <Kpi
          icon={<Percent size={17} />}
          etichetta="Occupazione media"
          valore={
            portafoglio && portafoglio.occupazione != null
              ? `${portafoglio.occupazione}%`
              : "—"
          }
          barra={
            portafoglio && portafoglio.occupazione != null
              ? { quota: portafoglio.occupazione / 100 }
              : null
          }
          dettaglio={
            portafoglio
              ? `${portafoglio.mese ? `a ${portafoglio.mese} · ` : ""}${portafoglio.piene} ${portafoglio.piene === 1 ? "casa piena" : "case piene"} oltre il 70%`
              : ""
          }
          tone={
            portafoglio &&
            portafoglio.occupazione != null &&
            portafoglio.occupazione >= 70
              ? "ok"
              : "plain"
          }
          caricamento={!portafoglio}
        />
        <Kpi
          icon={<Euro size={17} />}
          etichetta="Prezzo medio"
          valore={
            portafoglio && portafoglio.prezzo != null
              ? formatEur(portafoglio.prezzo)
              : "—"
          }
          dettaglio="a notte, media fra le case"
          caricamento={!portafoglio}
        />
        <Kpi
          icon={<Inbox size={17} />}
          etichetta="Da decidere"
          valore={overview ? String(daDecidere) : ""}
          dettaglio={
            overview
              ? daDecidere === 0
                ? "niente in sospeso"
                : [
                    overview.awaitingReplies > 0
                      ? `${overview.awaitingReplies} ${overview.awaitingReplies === 1 ? "ospite aspetta" : "ospiti aspettano"}`
                      : null,
                    overview.pendingActions > 0
                      ? `${overview.pendingActions} ${overview.pendingActions === 1 ? "proposta da confermare" : "proposte da confermare"}`
                      : null,
                  ]
                    .filter(Boolean)
                    .join(" · ")
              : ""
          }
          tone={daDecidere > 0 ? "accent" : "plain"}
          onClick={
            daDecidere > 0
              ? () =>
                  onGoTo(
                    (overview?.awaitingReplies ?? 0) > 0 ? "inbox" : "chat",
                  )
              : undefined
          }
          caricamento={!overview}
        />
      </GrigliaKpi>

      {/* ── La giornata: una lista sola, con i filtri ─────────────────── */}
      <Card className="min-w-0 max-w-full p-5">
        <IntestazioneRiquadro
          icon={<ListChecks size={15} />}
          titolo={
            etichetta
              ? `${etichetta} · ${formatDate(date, oggi)}`
              : formatDate(date, oggi)
          }
          azione={
            <div className="flex items-center gap-2">
              {date !== oggi ? (
                <Pill size="sm" variant="ghost" onClick={() => setDate(oggi)}>
                  Torna a oggi
                </Pill>
              ) : null}
              <div className="flex items-center gap-0.5 rounded-pill bg-surface-muted p-0.5">
                <button
                  type="button"
                  onClick={() => setDate(addDays(date, -1))}
                  aria-label="Giorno precedente"
                  className="grid size-7 place-items-center rounded-pill text-ink-soft transition-all duration-quick hover:bg-surface hover:text-ink active:scale-95"
                >
                  <ChevronLeft size={15} />
                </button>
                <button
                  type="button"
                  onClick={() => setDate(addDays(date, 1))}
                  aria-label="Giorno successivo"
                  className="grid size-7 place-items-center rounded-pill text-ink-soft transition-all duration-quick hover:bg-surface hover:text-ink active:scale-95"
                >
                  <ChevronRight size={15} />
                </button>
              </div>
            </div>
          }
        />

        <div className="mb-4 max-w-[520px]">
          <Segmented<Filtro>
            value={filtro}
            onChange={setFiltro}
            options={[
              {
                id: "tutto",
                label: "Tutto",
                count: overview
                  ? overview.checkIns.length +
                    overview.checkOuts.length +
                    overview.tasks.length
                  : undefined,
              },
              {
                id: "arrivi",
                label: "Arrivi",
                count: overview?.checkIns.length,
              },
              {
                id: "partenze",
                label: "Partenze",
                count: overview?.checkOuts.length,
              },
              { id: "lavori", label: "Pulizie", count: overview?.tasks.length },
            ]}
          />
        </div>

        {!overview ? (
          <div className="flex flex-col gap-2">
            <Skeleton className="h-14" />
            <Skeleton className="h-14" />
            <Skeleton className="h-14" />
          </div>
        ) : eventi.length === 0 ? (
          <p className="rounded-field bg-surface-muted px-4 py-3 text-[13.5px] text-ink-soft">
            {giornataVuota
              ? "Giornata tranquilla: nessun arrivo, nessuna partenza, niente da pulire."
              : "Niente di questo tipo, in questo giorno."}
          </p>
        ) : (
          <div className="grid grid-cols-[minmax(0,1fr)] gap-2 lg:grid-cols-2">
            {eventi.map((e) => (
              <Evento key={e.id} {...e} />
            ))}
          </div>
        )}
      </Card>

      {/* ── I sette giorni davanti ────────────────────────────────────── */}
      <Card className="p-5">
        <IntestazioneRiquadro
          icon={<CalendarRange size={15} />}
          titolo="I prossimi sette giorni"
          nota="arrivi · partenze · pulizie"
        />
        {!settimana ? (
          <Skeleton className="h-[104px]" />
        ) : (
          <div className="grid grid-cols-7 gap-2">
            {settimana.map((giorno) => (
              <GiornoDellaSettimana
                key={giorno.date}
                giorno={giorno}
                oggi={oggi}
                attivo={giorno.date === date}
                onClick={() => setDate(giorno.date)}
              />
            ))}
          </div>
        )}
      </Card>

      {/* ── Consigli, se ce ne sono ───────────────────────────────────── */}
      {suggestions.length > 0 ? (
        <Card
          interactive
          onClick={() => onGoTo("suggestions")}
          className="flex items-center gap-3 p-4"
        >
          <span className="grid size-10 shrink-0 place-items-center rounded-field bg-warn-soft text-warn">
            <Sun size={18} />
          </span>
          <span className="min-w-0 flex-1">
            <span className="block text-[14.5px] font-bold text-ink">
              {suggestions.length === 1
                ? "1 cosa da sistemare negli annunci"
                : `${suggestions.length} cose da sistemare negli annunci`}
            </span>
            <span className="block truncate text-[13px] text-ink-soft">
              {suggestions[0]!.titolo}
            </span>
          </span>
          <ChevronRight size={17} className="shrink-0 text-ink-faint" />
        </Card>
      ) : null}

      {giornataVuota && daDecidere === 0 && suggestions.length === 0 ? (
        <EmptyState
          icon={<Sparkles size={24} />}
          title="Tutto tranquillo"
          hint="Nessuno aspetta, niente da confermare, niente da sistemare."
          action={
            <Pill size="sm" variant="secondary" onClick={() => onGoTo("chat")}>
              Chiedi qualcosa a Otto
            </Pill>
          }
        />
      ) : null}

      {store.account?.killSwitch ? (
        <Card tone="danger" className="p-4">
          <p className="text-[13.5px] font-bold text-danger">
            Lo stop generale è attivo: non sto eseguendo nessuna modifica.
          </p>
        </Card>
      ) : null}
    </div>
  );
}

// ── Pezzi ───────────────────────────────────────────────────────────────────

const TIPI = {
  arrivi: { Icona: LogIn, classe: "bg-ok-soft text-ok", nome: "Arrivo" },
  partenze: {
    Icona: LogOut,
    classe: "bg-warn-soft text-warn",
    nome: "Partenza",
  },
  lavori: {
    Icona: Wrench,
    classe: "bg-accent-soft text-accent",
    nome: "Pulizia",
  },
} as const;

function Evento({
  tipo,
  titolo,
  sotto,
  ora,
  fatto,
  chi,
}: {
  tipo: keyof typeof TIPI;
  titolo: string;
  sotto: string;
  ora: string | null;
  fatto: boolean;
  chi: string | null;
}) {
  const t = TIPI[tipo];
  return (
    // `min-w-0`: i testi dentro non vanno a capo, e senza questo la griglia
    // misura la riga sul nome intero e la fa uscire dal riquadro (telefono).
    <div className="flex min-w-0 items-center gap-3 rounded-field bg-surface-muted px-3.5 py-2.5">
      {tipo === "lavori" ? (
        <span
          className={cx(
            "grid size-9 shrink-0 place-items-center rounded-field",
            t.classe,
          )}
        >
          <t.Icona size={16} />
        </span>
      ) : (
        <Avatar name={titolo} size={36} />
      )}
      <span className="min-w-0 flex-1">
        <span
          className={cx(
            "block truncate text-[14px] font-bold text-ink",
            fatto && "text-ink-faint line-through decoration-ink-faint",
          )}
        >
          {titolo}
        </span>
        <span className="block truncate text-[12.5px] text-ink-soft">
          <span className={cx("font-semibold", t.classe.split(" ")[1])}>
            {t.nome}
          </span>
          {" · "}
          {sotto}
        </span>
      </span>
      {chi ? (
        <span className="shrink-0 rounded-pill bg-surface px-2 py-0.5 text-[11.5px] font-bold text-ink-soft ring-1 ring-line">
          {chi}
        </span>
      ) : null}
      {ora ? (
        <span className="flex shrink-0 items-center gap-1 rounded-pill bg-surface px-2 py-0.5 text-[12px] font-bold tabular-nums text-ink-soft ring-1 ring-line">
          <Clock size={11} />
          {ora}
        </span>
      ) : null}
    </div>
  );
}

/**
 * Un giorno della striscia: quanti arrivi, quante partenze, quante pulizie.
 * Pallini con il numero: la settimana si legge con gli occhi, non contando.
 */
function GiornoDellaSettimana({
  giorno,
  oggi,
  attivo,
  onClick,
}: {
  giorno: DayOverview;
  oggi: IsoDate;
  attivo: boolean;
  onClick: () => void;
}) {
  const movimenti =
    giorno.checkIns.length + giorno.checkOuts.length + giorno.tasks.length;

  return (
    <button
      type="button"
      onClick={onClick}
      className={cx(
        "flex flex-col items-center gap-1.5 rounded-card px-1 py-3",
        "transition-all duration-calm ease-soft",
        attivo
          ? "bg-ink text-ink-inverse"
          : "bg-surface-muted text-ink hover:bg-surface-sunken",
      )}
    >
      <span
        className={cx(
          "text-[10px] font-bold uppercase tracking-[0.06em]",
          attivo ? "text-ink-inverse/60" : "text-ink-faint",
        )}
      >
        {GIORNI[weekday(giorno.date)]}
      </span>

      <span className="text-[18px] font-extrabold leading-none tabular-nums">
        {Number(giorno.date.slice(8, 10))}
      </span>

      {movimenti === 0 ? (
        <span
          className={cx(
            "text-[11px]",
            attivo ? "text-ink-inverse/50" : "text-ink-faint",
          )}
        >
          —
        </span>
      ) : (
        <span className="flex items-center gap-1.5">
          {/* Su telefono le colonne sono larghe 40 px: tre pallini con il
              numero non ci stanno e uscivano dalla casella. Un totale solo. */}
          <span
            className={cx(
              "text-[12px] font-bold tabular-nums sm:hidden",
              attivo ? "text-ink-inverse" : "text-ink",
            )}
          >
            {movimenti}
          </span>
          <span className="hidden items-center gap-1.5 sm:flex">
            <Conteggio
              valore={giorno.checkIns.length}
              classe="bg-ok"
              attivo={attivo}
            />
            <Conteggio
              valore={giorno.checkOuts.length}
              classe="bg-warn"
              attivo={attivo}
            />
            <Conteggio
              valore={giorno.tasks.length}
              classe="bg-accent"
              attivo={attivo}
            />
          </span>
        </span>
      )}

      <span
        className={cx(
          "text-[10px] font-bold",
          attivo ? "text-ink-inverse/70" : "text-accent",
        )}
      >
        {giorno.date === oggi ? "oggi" : " "}
      </span>
    </button>
  );
}

function Conteggio({
  valore,
  classe,
  attivo,
}: {
  valore: number;
  classe: string;
  attivo: boolean;
}) {
  if (valore === 0) return null;
  return (
    <span className="flex items-center gap-0.5">
      <span className={cx("size-1.5 rounded-full", classe)} />
      <span
        className={cx(
          "text-[11px] font-bold tabular-nums",
          attivo ? "" : "text-ink-soft",
        )}
      >
        {valore}
      </span>
    </span>
  );
}

/**
 * Quanto stanno rendendo **tutte** le case, questo mese.
 *
 * Era il numero che mancava. La panoramica qui sopra dice occupazione media e
 * prezzo medio — due medie — e non diceva un euro: per sapere quanto aveva
 * incassato in tutto, l'host doveva aprire i Conti una casa alla volta e
 * sommare a mano.
 *
 * Due cifre grandi e una riga sotto, perché le domande sono due e in
 * quest'ordine: «quanto è entrato» e «quanto me ne resta». Le notti libere
 * stanno in coda e solo finché il mese è aperto: a mese finito non sono più
 * un'occasione, sono un rimpianto.
 */
function ContoDelMese({ conto }: { conto: ProfittoPortafoglio | null }) {
  if (!conto) {
    return (
      <Card className="p-4">
        <Skeleton className="h-4 w-40" />
        <div className="mt-3 flex gap-8">
          <Skeleton className="h-7 w-32" />
          <Skeleton className="h-7 w-32" />
        </div>
      </Card>
    );
  }

  if (conto.immobili === 0) return null;

  const t = conto.totali;
  // L'occupazione si misura su quello che è **in vendita**. `nottiDisponibili`
  // sono i giorni del calendario, e al denominatore ci mettevano dentro anche
  // le notti che l'host si è tenuto: settembre 2026 usciva «85 su 120 · 71%»
  // quando in vendita ce n'erano 117 e l'occupazione vera era il 73%. È la
  // stessa riga sbagliata che c'era nella risposta in chat.
  const inVendita = t.nottiVendute + t.nottiLibere;
  const occupazione = Math.round(
    (t.nottiVendute / Math.max(1, inVendita)) * 100,
  );
  const meseAperto = conto.mese >= todayInRome().slice(0, 7);

  return (
    <Card className="p-4">
      <div className="flex items-baseline justify-between gap-3">
        <p className="text-[13px] font-medium text-ink-soft">
          Tutte le case, {nomeMese(conto.mese)}
        </p>
        {/* Sul telefono questa riga accanto al titolo andava a capo male:
            lì sta sotto i numeri, intera, come le altre righe. */}
        <span className="hidden text-[12px] tabular-nums text-ink-faint sm:inline">
          {t.nottiVendute} notti su {inVendita} in vendita · {occupazione}%
        </span>
      </div>

      <div className="mt-2.5 flex flex-wrap items-baseline gap-x-8 gap-y-2">
        <div>
          <p className="text-[22px] font-bold tabular-nums leading-tight text-ink">
            {formatEur(t.ricaviLordiCents)}
          </p>
          <p className="text-[12px] text-ink-faint">incassati</p>
        </div>
        <div>
          <p className="text-[22px] font-bold tabular-nums leading-tight text-ink">
            {formatEur(t.utileCasaCents)}
          </p>
          <p className="text-[12px] text-ink-faint">di utile</p>
        </div>
      </div>

      <p className="mt-2.5 text-[12.5px] tabular-nums text-ink-soft sm:hidden">
        {t.nottiVendute} notti vendute su {inVendita} · {occupazione}% occupate
      </p>

      {meseAperto && t.nottiLibere > 0 ? (
        <p className="mt-2.5 text-[12.5px] leading-relaxed text-ink-soft">
          Restano {t.nottiLibere} notti libere, che a listino varrebbero{" "}
          {formatEur(t.valoreNottiLibereCents)}.
        </p>
      ) : null}

      {/* Le bloccate si dicono a parte: non sono invendute, sono fuori
          mercato, e chi le vede nel conto deve sapere che non mancano. */}
      {t.nottiBloccate > 0 ? (
        <p className="mt-1 text-[12px] text-ink-faint">
          Altre {t.nottiBloccate}{" "}
          {t.nottiBloccate === 1 ? "notte è bloccata" : "notti sono bloccate"} a
          mano, fuori dal conto dell&apos;occupazione.
        </p>
      ) : null}

      {/* Le due cose che rendono il numero leggibile invece che solo grande. */}
      {t.stimatoCents > 0 ? (
        <p className="mt-1 text-[12px] text-ink-faint">
          {formatEur(t.stimatoCents)} dei costi sono stime, non numeri
          confermati.
        </p>
      ) : null}
      {conto.immobiliSenzaCalendario > 0 ? (
        <p className="mt-1 text-[12px] text-warn">
          Di {conto.immobiliSenzaCalendario}{" "}
          {conto.immobiliSenzaCalendario === 1 ? "casa" : "case"} non conosco
          nessuna notte di questo mese: il totale è più basso del vero.
        </p>
      ) : null}
    </Card>
  );
}
